import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
    Trophy, Medal, Award, Search, School, MapPin, Users, 
    TrendingUp, ChevronRight, Filter, Star, Globe, Building2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available sports for filtering
const SPORTS_LIST = [
    'Football', 'Basketball', 'Volleyball', 'Swimming', 'Athletics Track and Field',
    'Rugby', 'Tennis', 'Hockey', 'Cricket', 'Badminton', 'Table Tennis',
    'Handball', 'Netball', 'Baseball', 'Softball'
];

// Institution type labels
const INSTITUTION_LABELS = {
    'international_school': 'International School',
    'private_school': 'Private School',
    'government_public_school': 'Public School',
    'sports_academy': 'Sports Academy',
    'community_sports_club': 'Sports Club'
};

const SchoolLeaderboardPage = () => {
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSport, setSelectedSport] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('all');

    useEffect(() => {
        fetchSchools();
    }, []);

    const fetchSchools = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/school-rankings`);
            setSchools(response.data || []);
        } catch (error) {
            console.error('Failed to fetch schools:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get unique countries for filter
    const countries = useMemo(() => {
        const countrySet = new Set(schools.map(s => s.country).filter(Boolean));
        return Array.from(countrySet).sort();
    }, [schools]);

    // Get available sports (sports that at least one school offers)
    const availableSports = useMemo(() => {
        const sportSet = new Set();
        schools.forEach(school => {
            if (school.sports && Array.isArray(school.sports)) {
                school.sports.forEach(s => sportSet.add(s));
            } else if (school.sport) {
                sportSet.add(school.sport);
            }
        });
        return Array.from(sportSet).sort();
    }, [schools]);

    // Filter and rank schools by selected sport
    const rankedSchools = useMemo(() => {
        let filtered = schools;

        // Filter by country
        if (selectedCountry !== 'all') {
            filtered = filtered.filter(s => s.country === selectedCountry);
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(s => 
                s.school_name?.toLowerCase().includes(query) ||
                s.city?.toLowerCase().includes(query)
            );
        }

        // If a sport is selected, filter schools that offer that sport and sort by rank
        if (selectedSport !== 'all') {
            filtered = filtered.filter(school => {
                if (school.sports && Array.isArray(school.sports)) {
                    return school.sports.includes(selectedSport);
                }
                return school.sport === selectedSport;
            });

            // Sort by rank in the selected sport
            filtered = filtered.map(school => {
                const sportRank = school.sport_rankings?.[selectedSport] || {};
                return {
                    ...school,
                    displayRank: sportRank.rank || 999,
                    displayPoints: sportRank.points || 0,
                    displayWins: sportRank.wins || 0,
                    displayLosses: sportRank.losses || 0
                };
            }).sort((a, b) => a.displayRank - b.displayRank);
        } else {
            // Show overall best rank
            filtered = filtered.map(school => {
                let bestRank = school.rank || 999;
                let totalPoints = school.points || 0;
                let totalWins = school.wins || 0;
                let totalLosses = school.losses || 0;

                if (school.sport_rankings && Object.keys(school.sport_rankings).length > 0) {
                    totalPoints = 0;
                    totalWins = 0;
                    totalLosses = 0;
                    Object.values(school.sport_rankings).forEach(sr => {
                        if (sr.rank && sr.rank < bestRank) bestRank = sr.rank;
                        totalPoints += sr.points || 0;
                        totalWins += sr.wins || 0;
                        totalLosses += sr.losses || 0;
                    });
                }

                return {
                    ...school,
                    displayRank: bestRank,
                    displayPoints: totalPoints,
                    displayWins: totalWins,
                    displayLosses: totalLosses
                };
            }).sort((a, b) => a.displayRank - b.displayRank);
        }

        return filtered;
    }, [schools, selectedSport, selectedCountry, searchQuery]);

    // Get top 3 for podium
    const topThree = rankedSchools.slice(0, 3);

    // Get rank badge color and icon
    const getRankStyle = (rank) => {
        if (rank === 1) return { bg: 'bg-gradient-to-br from-yellow-500 to-amber-600', icon: Trophy, color: 'text-yellow-400' };
        if (rank === 2) return { bg: 'bg-gradient-to-br from-gray-300 to-gray-500', icon: Medal, color: 'text-gray-300' };
        if (rank === 3) return { bg: 'bg-gradient-to-br from-amber-600 to-amber-800', icon: Award, color: 'text-amber-500' };
        return { bg: 'bg-[#0d2847]', icon: null, color: 'text-primary' };
    };

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Header */}
            <header className="bg-[#0d2847]/80 backdrop-blur-sm border-b border-white/5 sticky top-0 z-50">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-[#0a1628]" />
                        </div>
                        <span className="font-heading font-bold text-xl tracking-tight">
                            NEXTGEN <span className="text-primary">TOURNAMENTS</span>
                        </span>
                    </Link>
                    <nav className="flex items-center gap-4">
                        <Link to="/tournaments">
                            <Button variant="ghost" size="sm">Tournaments</Button>
                        </Link>
                        <Link to="/talent">
                            <Button variant="ghost" size="sm">Talent Discovery</Button>
                        </Link>
                        <Link to="/auth">
                            <Button size="sm" className="bg-primary hover:bg-cyan-400 text-[#0a1628]">
                                Sign In
                            </Button>
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative py-16 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
                <div className="container mx-auto px-4 relative">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
                            <Trophy className="w-4 h-4 text-primary" />
                            <span className="text-sm text-primary font-medium">School Rankings</span>
                        </div>
                        <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mb-4">
                            School <span className="text-primary">Leaderboard</span>
                        </h1>
                        <p className="text-lg text-muted-foreground mb-8">
                            Discover the top-performing schools and academies across Africa. 
                            Filter by sport to find the best programs for your athletic journey.
                        </p>
                    </div>
                </div>
            </section>

            {/* Filters Section */}
            <section className="py-6 border-b border-white/5">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Sport Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-muted-foreground" />
                            <Select value={selectedSport} onValueChange={setSelectedSport}>
                                <SelectTrigger className="w-[200px] bg-[#0d2847] border-white/10" data-testid="sport-filter">
                                    <SelectValue placeholder="Select Sport" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    <SelectItem value="all">All Sports</SelectItem>
                                    {availableSports.map(sport => (
                                        <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Country Filter */}
                        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                            <SelectTrigger className="w-[180px] bg-[#0d2847] border-white/10" data-testid="country-filter">
                                <SelectValue placeholder="All Countries" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Countries</SelectItem>
                                {countries.map(country => (
                                    <SelectItem key={country} value={country}>{country}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search schools..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-[#0d2847] border-white/10"
                                data-testid="search-input"
                            />
                        </div>

                        <span className="text-sm text-muted-foreground ml-auto">
                            {rankedSchools.length} schools found
                        </span>
                    </div>
                </div>
            </section>

            {/* Sport Title */}
            {selectedSport !== 'all' && (
                <section className="py-8">
                    <div className="container mx-auto px-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                                <Trophy className="w-8 h-8 text-[#0a1628]" />
                            </div>
                            <div>
                                <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase">
                                    Top {selectedSport} Schools
                                </h2>
                                <p className="text-muted-foreground">
                                    Ranked by {selectedSport} performance
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Top 3 Podium */}
            {topThree.length >= 3 && !loading && (
                <section className="py-8">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto">
                            {/* 2nd Place */}
                            <div className="flex flex-col items-center pt-8">
                                <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                    <Medal className="w-10 h-10 text-[#0a1628]" />
                                </div>
                                <div className="bg-[#0d2847] border border-gray-400/30 p-4 rounded-xl text-center w-full">
                                    <span className="text-2xl font-bold text-gray-300">#2</span>
                                    <h3 className="font-heading font-bold text-lg mt-2 truncate" title={topThree[1]?.school_name}>
                                        {topThree[1]?.school_name}
                                    </h3>
                                    <p className="text-xs text-muted-foreground">{topThree[1]?.city}, {topThree[1]?.country}</p>
                                    <div className="mt-3 flex justify-center gap-4 text-sm">
                                        <span className="text-primary">{topThree[1]?.displayPoints} pts</span>
                                        <span className="text-success">{topThree[1]?.displayWins}W</span>
                                    </div>
                                </div>
                            </div>

                            {/* 1st Place */}
                            <div className="flex flex-col items-center">
                                <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-full flex items-center justify-center mb-4 shadow-xl ring-4 ring-yellow-400/30">
                                    <Trophy className="w-12 h-12 text-[#0a1628]" />
                                </div>
                                <div className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 border border-yellow-500/30 p-6 rounded-xl text-center w-full">
                                    <span className="text-3xl font-bold text-yellow-400">#1</span>
                                    <h3 className="font-heading font-bold text-xl mt-2 truncate" title={topThree[0]?.school_name}>
                                        {topThree[0]?.school_name}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">{topThree[0]?.city}, {topThree[0]?.country}</p>
                                    <div className="mt-3 flex justify-center gap-4 text-sm">
                                        <span className="text-primary font-bold">{topThree[0]?.displayPoints} pts</span>
                                        <span className="text-success font-bold">{topThree[0]?.displayWins}W</span>
                                    </div>
                                </div>
                            </div>

                            {/* 3rd Place */}
                            <div className="flex flex-col items-center pt-12">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                    <Award className="w-8 h-8 text-[#0a1628]" />
                                </div>
                                <div className="bg-[#0d2847] border border-amber-500/30 p-4 rounded-xl text-center w-full">
                                    <span className="text-2xl font-bold text-amber-500">#3</span>
                                    <h3 className="font-heading font-bold text-lg mt-2 truncate" title={topThree[2]?.school_name}>
                                        {topThree[2]?.school_name}
                                    </h3>
                                    <p className="text-xs text-muted-foreground">{topThree[2]?.city}, {topThree[2]?.country}</p>
                                    <div className="mt-3 flex justify-center gap-4 text-sm">
                                        <span className="text-primary">{topThree[2]?.displayPoints} pts</span>
                                        <span className="text-success">{topThree[2]?.displayWins}W</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Full Leaderboard Table */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    <div className="bg-[#0d2847] border border-white/5 rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-white/5">
                            <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-primary" />
                                {selectedSport !== 'all' ? `${selectedSport} Rankings` : 'Overall Rankings'}
                            </h3>
                        </div>

                        {loading ? (
                            <div className="py-20 text-center text-muted-foreground">
                                Loading rankings...
                            </div>
                        ) : rankedSchools.length === 0 ? (
                            <div className="py-20 text-center text-muted-foreground">
                                No schools found matching your criteria
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/5 bg-[#0a1628]">
                                            <th className="text-left p-4 text-xs tracking-widest uppercase text-muted-foreground w-20">Rank</th>
                                            <th className="text-left p-4 text-xs tracking-widest uppercase text-muted-foreground">School</th>
                                            <th className="text-left p-4 text-xs tracking-widest uppercase text-muted-foreground">Type</th>
                                            <th className="text-left p-4 text-xs tracking-widest uppercase text-muted-foreground">Location</th>
                                            <th className="text-left p-4 text-xs tracking-widest uppercase text-muted-foreground">Sports</th>
                                            <th className="text-center p-4 text-xs tracking-widest uppercase text-muted-foreground">Record</th>
                                            <th className="text-right p-4 text-xs tracking-widest uppercase text-muted-foreground">Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rankedSchools.map((school, index) => {
                                            const rankStyle = getRankStyle(index + 1);
                                            const RankIcon = rankStyle.icon;
                                            
                                            return (
                                                <tr 
                                                    key={school.id} 
                                                    className={`border-b border-white/5 hover:bg-white/5 transition-colors ${index < 3 ? 'bg-white/[0.02]' : ''}`}
                                                    data-testid={`school-row-${index + 1}`}
                                                >
                                                    <td className="p-4">
                                                        <div className={`w-10 h-10 ${rankStyle.bg} rounded-lg flex items-center justify-center`}>
                                                            {RankIcon ? (
                                                                <RankIcon className={`w-5 h-5 ${index === 0 ? 'text-[#0a1628]' : 'text-white'}`} />
                                                            ) : (
                                                                <span className={`font-bold ${rankStyle.color}`}>#{index + 1}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                                                <School className="w-5 h-5 text-primary" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium">{school.school_name}</p>
                                                                <p className="text-xs text-muted-foreground">{school.athletes_count || 0} athletes</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-4">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                                            school.institution_type === 'international_school' ? 'bg-purple-500/20 text-purple-400' :
                                                            school.institution_type === 'private_school' ? 'bg-blue-500/20 text-blue-400' :
                                                            school.institution_type === 'government_public_school' ? 'bg-emerald-500/20 text-emerald-400' :
                                                            school.institution_type === 'community_sports_club' ? 'bg-pink-500/20 text-pink-400' :
                                                            'bg-orange-500/20 text-orange-400'
                                                        }`}>
                                                            {school.institution_type === 'international_school' && <Globe className="w-3 h-3" />}
                                                            {school.institution_type === 'private_school' && <Building2 className="w-3 h-3" />}
                                                            {school.institution_type === 'sports_academy' && <Users className="w-3 h-3" />}
                                                            {INSTITUTION_LABELS[school.institution_type] || 'Academy'}
                                                        </span>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <MapPin className="w-4 h-4" />
                                                            {school.city}, {school.country}
                                                        </div>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex flex-wrap gap-1">
                                                            {selectedSport !== 'all' ? (
                                                                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">
                                                                    {selectedSport}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    {(school.sports || [school.sport]).filter(Boolean).slice(0, 2).map((s, i) => (
                                                                        <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                                                                            {s}
                                                                        </span>
                                                                    ))}
                                                                    {(school.sports?.length || 1) > 2 && (
                                                                        <span className="px-2 py-0.5 bg-white/10 text-muted-foreground text-xs rounded">
                                                                            +{(school.sports?.length || 1) - 2}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <span className="text-success">{school.displayWins}W</span>
                                                        <span className="text-muted-foreground mx-1">-</span>
                                                        <span className="text-destructive">{school.displayLosses}L</span>
                                                    </td>
                                                    <td className="p-4 text-right">
                                                        <span className="font-mono font-bold text-xl text-primary">
                                                            {school.displayPoints?.toLocaleString() || 0}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Sport Categories Quick Links */}
            <section className="py-12 border-t border-white/5">
                <div className="container mx-auto px-4">
                    <h3 className="font-heading font-bold text-xl uppercase mb-6 text-center">
                        Browse by Sport
                    </h3>
                    <div className="flex flex-wrap justify-center gap-3">
                        {availableSports.slice(0, 10).map(sport => (
                            <Button
                                key={sport}
                                variant={selectedSport === sport ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setSelectedSport(sport)}
                                className={selectedSport === sport 
                                    ? 'bg-primary hover:bg-cyan-400 text-[#0a1628]' 
                                    : 'border-white/10 hover:border-primary/50'
                                }
                                data-testid={`sport-btn-${sport.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {sport}
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 border-t border-white/5 bg-[#0d2847]/50">
                <div className="container mx-auto px-4 text-center">
                    <p className="text-sm text-muted-foreground">
                        © 2026 NextGen Tournaments. All rights reserved.
                    </p>
                    <div className="flex justify-center gap-6 mt-4">
                        <Link to="/tournaments" className="text-sm text-muted-foreground hover:text-primary">
                            Tournaments
                        </Link>
                        <Link to="/talent" className="text-sm text-muted-foreground hover:text-primary">
                            Talent Discovery
                        </Link>
                        <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary">
                            Sign Up
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SchoolLeaderboardPage;
